'use client';
import gsap from 'gsap';
import React, { useRef } from 'react';
import useTransitionContext from '@/components/providers/context/transition-context';
import { useGSAP } from '@gsap/react';
import useIsomorphicLayoutEffect from '@/lib/hooks/isomorphic-layout-effect';
import ScrollTrigger from 'gsap/ScrollTrigger';

function AnimateInOut({
    children,
    durationIn,
    durationOut,
    delay,
    delayOut,
    from,
    to,
    skipOutro,
    watch,
    start,
    end,
    scrub,
    markers,
}: Readonly<{
    children: React.ReactNode;
    durationIn: number;
    durationOut: number;
    delay: number;
    delayOut: number;
    from: Record<string, any>;
    to: Record<string, any>;
    skipOutro?: boolean;
    watch?: boolean;
    start: string;
    end: string;
    scrub: boolean;
    markers?: boolean;
}>) {
    const { timeline } = useTransitionContext();
    const element = useRef(null);

    useIsomorphicLayoutEffect(() => {
        // This is needed only in Stackblitz
        gsap.registerPlugin(ScrollTrigger);
        gsap.registerPlugin(useGSAP);
    }, []);

    useGSAP(
        () => {
            const scrollTrigger = watch
                ? {
                      scrollTrigger: {
                          trigger: element.current,
                          start,
                          end,
                          scrub,
                          markers: markers,
                      },
                  }
                : {};

            const ctx = gsap.context(() => {
                /* Intro animation */
                gsap.to(element.current, {
                    ...to,
                    delay,
                    duration: durationIn,
                    ...scrollTrigger,
                    onUpdate: () => {
                        timeline.seek(timeline.time());
                    },
                });

                /* Outro animation */
                if (!skipOutro) {
                    timeline.add(
                        gsap.to(element.current, {
                            ...from,
                            delay: delayOut,
                            duration: durationOut,
                        }),
                        0,
                    );
                }
            }, element);

            return () => ctx.revert();
        },
        {
            scope: element,
        },
    );

    return (
        <div ref={element} style={from}>
            {children}
        </div>
    );
}

export default React.memo(AnimateInOut);
